import { Fragment, useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr, wsBase } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { answerWithPasskey, answerWithPasskeyPrf, passkeyError, NO_PRF } from "@/lib/webauthn";
import { sessionKeyForUnlock } from "@/lib/vault";
import { APP_NAME } from "@/config";
import { StatusBadge, Segmented, Label11, Loading, usd, int, plural } from "@/components/common";
import { EngineWithLogo } from "@/components/ProviderLogo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Lock, LogOut, Loader2, FileCheck, Fingerprint, ImagePlus, X, AppWindow, ArrowUp, Square, Share, FileText, SquareTerminal, CircleAlert, TriangleAlert, History, ChevronRight, BookOpen } from "lucide-react";
import { useFlags } from "@/lib/flags";
import { prepareImage, MAX_IMAGES, IMAGE_TYPES } from "@/lib/images";
import { Entry, WorkingLine, COMMANDS, duration, tidy } from "@/components/terminal";
import { Inline } from "@/lib/inline";
import { GraphiteScope } from "@/components/Graphite";
import DiffViewer from "@/components/DiffViewer";
import AskDocuments, { useReferences } from "@/components/AskDocuments";
import { cn } from "@/lib/utils";
import { Disclosure } from "@/components/ChangeReport";
import { WelcomeCard, DocTranscript, DocWorking, DocTag } from "@/components/DocumentsView";
import { KIND_LABELS, kindOf, isDocuments, versionsOf, versionDetail, docName, docFolder, DOC_STATE } from "@/lib/workKinds";

// A turn's documents and its version, rebuilt from the history when the session is unlocked: each user message starts a
// turn, and the turn's replies, changed files and commit (by time, up to the next message) follow it, in the order they
// arrive live
function withHistory(msgs, fileEvents, commits) {
  const turns = [];
  for (const m of msgs) {
    const e = { kind: m.role === "user" ? "user" : "assistant", text: m.role === "user" ? m.text : tidy(m.text),
      ...(m.images ? { images: m.images.map((i) => ({ id: i.id })) } : {}) };
    if (m.role === "user" || !turns.length) turns.push({ ts: m.ts, said: [], files: [], commits: [] });
    turns[turns.length - 1].said.push(e);
  }
  const turnAt = (ts) => { let at = 0; turns.forEach((tn, i) => { if (tn.ts <= ts) at = i; }); return turns[at]; };
  if (turns.length) {
    [...fileEvents].sort((a, b) => a.ts.localeCompare(b.ts)).forEach((f) => turnAt(f.ts).files.push({ kind: "file", path: f.path, added: f.added, removed: f.removed }));
    [...commits].sort((a, b) => a.ts.localeCompare(b.ts)).forEach((c) => turnAt(c.ts).commits.push({ kind: "commit", sha: c.sha, message: c.message, files: c.files }));
  }
  return turns.flatMap((tn) => [...tn.said, ...tn.files, ...tn.commits]);
}

// design_handoff_crewbench/screens/Session Workspace.dc.html: the independent professional's session. A 48px header that
// stays dark in both themes, the lock card, then the terminal (or the diff viewer, opened from a changed file) and a
// 300px side panel.

function Banner({ session, objective, memoryVersion, base, mcp }) {
  return (
    <div className="border rounded-lg px-3.5 py-2.5 mb-1.5 text-muted-foreground text-[12px] leading-[19px]" data-testid="terminal-banner">
      <div className="text-foreground">{APP_NAME} session {session.short_id} · {session.model}</div>
      <div>Objective: {objective || "General"}{memoryVersion ? ` · shared memory v${memoryVersion} loaded` : ""}</div>
      <div>Branch {session.branch} from {base || "main"} · committed for you after each turn</div>
      {session.folders && <div data-testid="terminal-folders">Only these folders: {session.folders.join(", ")} · changes elsewhere are put back</div>}
      {mcp?.length > 0 && <div data-testid="terminal-mcp">MCP servers: {mcp.join(", ")}</div>}
    </div>
  );
}

// a $ field, as the handoff draws money inputs
function Dollars({ value, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-1.5 self-start h-8 px-2.5 rounded-md border border-gr-control-border focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring">
      <span className="text-muted-foreground">$</span>
      <input type="number" min="1" step="1" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-[72px] border-0 bg-transparent text-[13px] outline-none tabular-nums" />
    </label>
  );
}

export default function Workspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [unlockToken, setUnlockToken] = useState(null);
  const [mcp, setMcp] = useState([]);  // the founder's MCP servers this unlock gave the engine
  const [pw, setPw] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  // the terminal transcript: user, assistant, tools, file, commit, error, info and done lines
  const [entries, setEntries] = useState([]);
  const [buffer, setBuffer] = useState("");
  const [working, setWorking] = useState(null); // { since, tool } while a turn runs
  const [stopping, setStopping] = useState(false); // Stop was pressed and the turn hasn't ended yet
  const [input, setInput] = useState("");
  const flags = useFlags();
  const [ports, setPorts] = useState(null); // the app preview's port list (backend/preview.py), while its dialog is open
  const [portDraft, setPortDraft] = useState("");
  const [images, setImages] = useState([]); // screenshots waiting to go with the next message
  const imageUrls = useRef([]); // object URLs of the history's images, revoked when the session closes
  const fileRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [historyAt, setHistoryAt] = useState(-1);
  const [dialog, setDialog] = useState(null);  // "propose" | "cap" | "report"
  const [proposeText, setProposeText] = useState("");
  const [capDraft, setCapDraft] = useState("");
  const [report, setReport] = useState(null);  // {report, pull_request} after Submit for review
  const [submitting, setSubmitting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [tab, setTab] = useState("files");
  // the diff viewer replaces the terminal pane; the terminal stays mounted, with its scroll and its stream
  const [view, setView] = useState("term");
  const [diffPath, setDiffPath] = useState(null);
  const [diffTurn, setDiffTurn] = useState("all");
  const [askOpen, setAskOpen] = useState(false);  // Ask your documents, in the side panel's place
  const qc = useQueryClient();
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const bufferRef = useRef("");
  const workingRef = useRef(null);

  const sessionQ = useQuery({ queryKey: ["session", id], queryFn: async () => (await api.get(`/sessions/${id}`)).data.session, refetchInterval: unlockToken ? false : 4000 });
  const filesQ = useQuery({ queryKey: ["files", id], enabled: !!unlockToken, queryFn: async () => (await api.get(`/sessions/${id}/files`)).data });
  const session = sessionQ.data;
  // session controls (backend set_controls): the engine's models the founder allows, and each one's effort levels
  // the whole response, cached as New session caches it under the same key (a list here crashed the page coming from there)
  const enginesQ = useQuery({ queryKey: ["engines"], enabled: !!unlockToken, queryFn: async () => (await api.get("/engines")).data });
  const engineModels = (enginesQ.data?.engines || []).find((e) => e.engine === session?.engine)?.models || [];
  const setControls = async (change, said) => {
    try {
      await api.put(`/sessions/${id}/controls`, change);
      await sessionQ.refetch();
      if (said) push({ kind: "info", text: said });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const { data: objs } = useQuery({ queryKey: ["objectives"], enabled: !!session, queryFn: async () => (await api.get("/objectives")).data });
  const { data: projects } = useQuery({ queryKey: ["projects"], enabled: !!session, queryFn: async () => (await api.get("/projects")).data });
  const { data: memory } = useQuery({ queryKey: ["memory", session?.project_id], enabled: !!session?.project_id,
                                      queryFn: async () => (await api.get(`/memory?project_id=${session.project_id}`)).data });

  // the documents view (design_handoff_documents_view): the session's kind of work picks it, and nobody switches it
  // (owner, 2026-10-14): code opens the terminal, legal and writing the documents view
  const workKind = kindOf(sessionQ.data);
  const docs = isDocuments(workKind);
  // each changed document's state (new, changed, removed), from the changes the diff viewer shows
  const docIndexQ = useQuery({
    queryKey: ["diff", id, "index", "all"], enabled: !!unlockToken && docs, staleTime: 30000,
    queryFn: async () => (await api.get(`/sessions/${id}/diff`, { params: { turn: "all" }, headers: { "X-Unlock-Token": unlockToken } })).data,
  });

  // the PDFs of Ask your documents: the header counts them, the welcome card mentions them
  const refsQ = useReferences(id, unlockToken);
  const pdfCount = refsQ.data?.length || 0;

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [entries, buffer, working, docs]);

  const push = (...lines) => setEntries((all) => [...all, ...lines]);
  const startWorking = () => { workingRef.current = { since: Date.now(), tool: null }; setWorking(workingRef.current); };
  const stopWorking = () => { workingRef.current = null; setWorking(null); setStopping(false); };
  // streamed text becomes a line of its own before whatever comes next (a tool, a file, the end)
  const flush = () => {
    const text = tidy(bufferRef.current);
    bufferRef.current = "";
    setBuffer("");
    if (text) push({ kind: "assistant", text });
  };

  // called on unlock only; a plain function always sees the current state and queries
  const connectWs = (token) => {
    const ws = new WebSocket(`${wsBase()}/ws/session/${id}?token=${token}`);
    wsRef.current = ws;
    ws.onmessage = (msg) => {
      const ev = JSON.parse(msg.data);
      if (ev.type === "text_delta") { bufferRef.current += ev.text; setBuffer(bufferRef.current); }
      else if (ev.type === "tool_activity") {
        flush();
        if (workingRef.current) { workingRef.current = { ...workingRef.current, tool: ev.tool }; setWorking(workingRef.current); }
        setEntries((all) => {
          const last = all[all.length - 1];
          if (last?.kind === "tools") {
            return [...all.slice(0, -1), { ...last, counts: { ...last.counts, [ev.tool]: (last.counts[ev.tool] || 0) + 1 } }];
          }
          return [...all, { kind: "tools", counts: { [ev.tool]: 1 } }];
        });
      }
      else if (ev.type === "file") { flush(); push({ kind: "file", path: ev.path, added: ev.added, removed: ev.removed }); }
      else if (ev.type === "commit") {
        flush(); push({ kind: "commit", sha: ev.sha, message: ev.message, files: ev.files }); filesQ.refetch();
        qc.invalidateQueries({ queryKey: ["diff", id] });  // a new turn's commit: the branch's diff changed
      }
      else if (ev.type === "turn_complete") {
        flush();
        if (workingRef.current) push({ kind: "done", text: `Worked for ${duration(Date.now() - workingRef.current.since)}` });
        stopWorking();
        sessionQ.refetch(); filesQ.refetch();
      } else if (ev.type === "stopped") { flush(); push({ kind: "stopped", text: "Stopped. What it changed so far is committed." }); }
      else if (ev.type === "outside") {  // changed outside the session's folders: not committed (backend/session_folders.py)
        flush();
        const more = ev.count > ev.paths.length ? ` and ${ev.count - ev.paths.length} more` : "";
        push({ kind: "info", text: `Put back ${ev.count === 1 ? "a change" : `${ev.count} changes`} outside this session's folders: ${ev.paths.join(", ")}${more}.` });
      }
      else if (ev.type === "notice") { flush(); push({ kind: "notice", text: ev.message, server: ev.server }); }  // e.g. an MCP server that didn't start
      else if (ev.type === "error") { flush(); push({ kind: "error", text: ev.message, retry: !!workingRef.current }); stopWorking(); }
      else if (ev.type === "locked") { toast.warning("Session locked: it was idle. Its work is backed up."); teardown(); }
      else if (ev.type === "ended") { toast.info("Session ended."); navigate("/team"); }
    };
    ws.onclose = () => {};
  };

  const teardown = () => {
    wsRef.current?.close(); wsRef.current = null; setUnlockToken(null);
    qc.removeQueries({ queryKey: ["diff", id] }); qc.removeQueries({ queryKey: ["ask", id] });  // nothing of the session's content stays
    setView("term"); setAskOpen(false);
    imageUrls.current.forEach((u) => URL.revokeObjectURL(u)); imageUrls.current = []; setImages([]);
    setEntries([]); bufferRef.current = ""; setBuffer(""); stopWorking(); sessionQ.refetch();
  };

  const openDiff = (path) => { setDiffPath(path); setDiffTurn("all"); setView("diff"); };

  // with a passkey, sessions unlock only with it (backend/passkeys.py); protected ones always do (backend/vault.py)
  const withPasskey = user?.passkeys > 0 || user?.sessions_protected;
  const unlock = async () => {
    if (!withPasskey && !pw) return;
    setUnlocking(true);
    try {
      let body = { password: pw };
      if (withPasskey) {
        const { data: got } = await api.post(`/sessions/${id}/unlock/options`);
        if (got.vault) {  // the passkey's PRF output opens the session's key here, in the browser
          const { credential, prf } = await answerWithPasskeyPrf(got.options);
          if (!prf) throw new Error(NO_PRF);
          body = { passkey: { challenge_id: got.challenge_id, credential },
            session_key: await sessionKeyForUnlock(got.vault, credential.id, prf, user.id, id) };
        } else {
          body = { passkey: { challenge_id: got.challenge_id, credential: await answerWithPasskey(got.options) } };
        }
      }
      const { data } = await api.post(`/sessions/${id}/unlock`, body);
      setUnlockToken(data.unlock_token);
      setMcp(data.mcp_servers || []);
      setPw("");
      const msgs = (await api.get(`/sessions/${id}/messages`, { headers: { "X-Unlock-Token": data.unlock_token } })).data.messages;
      const saved = (await api.get(`/sessions/${id}/files`)).data;
      setEntries(withHistory(msgs, saved.files || [], saved.commits || []));
      loadImages(msgs, data.unlock_token);
      setHistory(msgs.filter((m) => m.role === "user").map((m) => m.text));
      connectWs(data.unlock_token);
      sessionQ.refetch(); filesQ.refetch();
      toast.success("Session unlocked.");
      setTimeout(() => inputRef.current?.focus(), 0);
    } catch (e) { toast.error(passkeyError(e) || apiErr(e)); } finally { setUnlocking(false); }
  };

  // the app the session runs, in a tab of its own on the preview domain (docs/APP_PREVIEW_PLAN.md)
  const openPreviewDialog = async () => {
    setDialog("preview"); setPorts(null);
    try {
      const found = (await api.get(`/sessions/${id}/preview/ports`, { headers: { "X-Unlock-Token": unlockToken } })).data.ports;
      setPorts(found);
      if (found.length && !portDraft) setPortDraft(String(found[0]));
    } catch (e) { setPorts([]); toast.error(apiErr(e)); }
  };
  const openPreview = async (port) => {
    const tab = window.open("", "_blank");  // opened now, while the click counts, then sent to the preview
    try {
      const { url } = (await api.post(`/sessions/${id}/preview`, { port: Number(port) }, { headers: { "X-Unlock-Token": unlockToken } })).data;
      if (tab) { tab.opener = null; tab.location.replace(url); } else window.open(url, "_blank", "noopener");
      setDialog(null);
    } catch (e) { tab?.close(); toast.error(apiErr(e)); }
  };

  // the history's screenshots: fetched with the unlock token, shown from memory only
  const loadImages = async (msgs, token) => {
    for (const m of msgs.filter((x) => x.images)) {
      for (const i of m.images) {
        try {
          const blob = (await api.get(`/sessions/${id}/images/${i.id}`, { headers: { "X-Unlock-Token": token }, responseType: "blob" })).data;
          const url = URL.createObjectURL(blob);
          imageUrls.current.push(url);
          setEntries((list) => list.map((e) => (e.images ? { ...e, images: e.images.map((x) => (x.id === i.id ? { ...x, url } : x)) } : e)));
        } catch { /* left as a placeholder */ }
      }
    }
  };

  const addImages = async (files) => {
    const list = [...files].filter((f) => IMAGE_TYPES.includes(f.type));
    if (!list.length) return false;
    if (images.length + list.length > MAX_IMAGES) { toast.error(`At most ${MAX_IMAGES} images per message.`); return true; }
    try {
      const ready = await Promise.all(list.map(prepareImage));
      setImages((now) => [...now, ...ready].slice(0, MAX_IMAGES));
    } catch (e) { toast.error(e.message || "This image couldn't be read."); }
    return true;
  };
  const onPaste = (e) => {
    const files = [...(e.clipboardData?.files || [])];
    if (files.some((f) => IMAGE_TYPES.includes(f.type))) { e.preventDefault(); addImages(files); }
  };
  const onDrop = (e) => {
    if (e.dataTransfer?.files?.length) { e.preventDefault(); addImages(e.dataTransfer.files); }
  };

  const lock = async () => { try { await api.post(`/sessions/${id}/lock`); } catch {} teardown(); toast.success("Session locked. Its work is backed up."); };
  const end = async () => {
    if (ending) return;  // one End at a time: it builds the final change report
    setEnding(true);
    try { await api.post(`/sessions/${id}/end`); } catch {}
    navigate("/team");
  };
  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    push({ kind: "info", text: "Building the change report…" });
    try {
      const { data } = await api.post(`/sessions/${id}/submit`);
      setReport({ report: data.report, pull_request: data.pull_request }); setDialog("report"); sessionQ.refetch();
      push({ kind: "info", text: docs ? "Submitted for review. Your founder has the change report."
        : data.pull_request?.number ? `Submitted for review. The change report is pull request #${data.pull_request.number}'s description.`
        : "Submitted for review. The founder gets this change report." });
    } catch (e) { push({ kind: "error", text: apiErr(e) }); toast.error(apiErr(e)); }
    setSubmitting(false);
  };
  const propose = async (text) => {
    try {
      await api.post("/memory/proposals", { project_id: session.project_id, session_id: id, text });
      setDialog(null); setProposeText("");
      push({ kind: "info", text: "Proposed to shared memory. Waiting for a founder to approve it." });
      toast.success("Proposed to shared memory.");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const setCap = async (dollars) => {
    try {
      await api.put(`/sessions/${id}/cap`, { budget_cap_usd: dollars }); sessionQ.refetch(); setDialog(null);
      push({ kind: "info", text: `${docs ? "Session budget" : "Session cap"} set to $${dollars.toFixed(2)}.` });
    } catch (e) { push({ kind: "error", text: apiErr(e) }); toast.error(apiErr(e)); }
  };

  const runCommand = (line) => {
    const [cmd, ...rest] = line.trim().split(/\s+/);
    const arg = line.trim().slice(cmd.length).trim();
    if (cmd === "/help") push({ kind: "info", text: COMMANDS.map(([c, d]) => `${c.padEnd(10)}${d}`).join("\n") });
    else if (cmd === "/submit") submit();
    else if (cmd === "/propose") arg ? propose(arg) : push({ kind: "error", text: "Usage: /propose <text>" });
    else if (cmd === "/cap") { const v = Number(rest[0]); v >= 1 ? setCap(v) : push({ kind: "error", text: "Usage: /cap <dollars>, for example /cap 30" }); }
    else if (cmd === "/stop") working ? stopTurn() : push({ kind: "info", text: "Nothing is running." });
    else if (cmd === "/lock") lock();
    else if (cmd === "/end") end();
    else if (cmd === "/clear") setEntries([]);
    else push({ kind: "error", text: `Unknown command ${cmd}. /help lists them.` });
  };

  // the Stop button, Esc or /stop: the engine stops where it is (backend session_routes._stop_turn)
  const stopTurn = () => {
    if (!workingRef.current || stopping) return;
    wsRef.current?.send(JSON.stringify({ type: "stop" }));
    setStopping(true);
  };

  const send = () => {
    const text = input.trim();
    if (!text) return;
    setHistory((h) => (h[h.length - 1] === text ? h : [...h, text]));
    setHistoryAt(-1);
    setInput("");
    if (text.startsWith("/")) { runCommand(text); return; }
    if (working) return;
    push({ kind: "user", text, ...(images.length ? { images: images.map((i) => ({ url: i.url })) } : {}) });
    wsRef.current?.send(JSON.stringify({ type: "message", text, ...(images.length ? { images: images.map((i) => ({ data: i.data })) } : {}) }));
    setImages([]);
    bufferRef.current = ""; setBuffer("");
    startWorking();
  };

  const retry = () => {
    const last = [...entries].reverse().find((e) => e.kind === "user");
    if (!last || workingRef.current) return;
    push({ kind: "user", text: last.text });
    wsRef.current?.send(JSON.stringify({ type: "message", text: last.text }));
    bufferRef.current = ""; setBuffer("");
    startWorking();
  };

  // the prompt grows with its text, like a terminal line that wraps
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  }, [input]);

  const matches = input.startsWith("/") && !input.includes(" ")
    ? COMMANDS.filter(([c]) => c.startsWith(input.toLowerCase())) : [];

  const onKeyDown = (e) => {
    if (e.key === "Escape" && working) { e.preventDefault(); stopTurn(); return; }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); return; }
    if (e.key === "Tab" && matches.length) { e.preventDefault(); setInput(`${matches[0][0]} `); return; }
    const el = e.currentTarget;
    const firstLine = !input.slice(0, el.selectionStart).includes("\n");
    const lastLine = !input.slice(el.selectionEnd).includes("\n");
    if (e.key === "ArrowUp" && firstLine && history.length) {
      e.preventDefault();
      const at = historyAt === -1 ? history.length - 1 : Math.max(0, historyAt - 1);
      setHistoryAt(at); setInput(history[at]);
    } else if (e.key === "ArrowDown" && lastLine && historyAt !== -1) {
      e.preventDefault();
      const at = historyAt + 1;
      if (at >= history.length) { setHistoryAt(-1); setInput(""); } else { setHistoryAt(at); setInput(history[at]); }
    }
  };

  // leaving the page (or ending the session) drops the stream and every diff held for it
  useEffect(() => () => { wsRef.current?.close(); qc.removeQueries({ queryKey: ["diff", id] }); qc.removeQueries({ queryKey: ["ask", id] }); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!session) return <GraphiteScope className="min-h-screen grid place-items-center bg-background"><Loading /></GraphiteScope>;

  const spent = session.spent_usd || 0;
  const cap = session.budget_cap_usd || 0;
  const pct = cap ? Math.min(100, (spent / cap) * 100) : 0;
  const objective = (objs?.objectives || []).find((o) => o.id === session.objective_id)?.title;
  const base = (projects?.projects || []).find((p) => p.id === session.project_id)?.base_branch;
  const files = filesQ.data?.files || [];
  const commits = filesQ.data?.commits || [];
  const r = report?.report;
  const pr = report?.pull_request;
  const headerButton = "border-gr-control-border text-foreground";
  // versions: the turns that saved something, numbered from 1 (lib/workKinds.js); each file event is in the first
  // version saved at or after it
  const versions = versionsOf(commits);
  const versionAt = (ts) => versions.find((v) => (v.ts || "") >= ts)?.n;
  const pathVersions = {};
  for (const f of files) { const n = versionAt(f.ts); if (n) (pathVersions[f.path] ||= new Set()).add(n); }
  const statusOf = Object.fromEntries((docIndexQ.data?.files || []).map((f) => [f.path, f.status]));
  // new only in the first version that touched it, removed only in the last
  const stateOf = (path, n) => {
    const ns = [...(pathVersions[path] || [])];
    if (statusOf[path] === "added" && (!n || !ns.length || n === Math.min(...ns))) return "new";
    if (statusOf[path] === "deleted" && (!n || !ns.length || n === Math.max(...ns))) return "removed";
    return "changed";
  };
  const sideDocs = Object.keys(pathVersions).sort((a, b) => docName(a).localeCompare(docName(b)))
    .map((path) => ({ path, ns: [...pathVersions[path]].sort((a, b) => a - b) }));
  const versionsLabel = (ns) => (ns.length === 1 ? `Version ${ns[0]}` : `Versions ${ns.slice(0, -1).join(", ")} and ${ns[ns.length - 1]}`);
  const openDoc = (path, n) => {
    const turn = versions.find((v) => v.n === n)?.turn;
    setDiffPath(path); setDiffTurn(turn ? String(turn) : "all"); setView("diff");
  };
  const out = cap > 0 && spent >= cap;
  const low = !out && cap > 0 && spent / cap >= 0.9;
  const modelLabel = engineModels.find((m) => m.model_id === session.model)?.label || session.model;

  return (
    <GraphiteScope className="h-screen flex flex-col bg-background">
      {/* the header stays dark in both themes (the handoff's keep-dark) */}
      <header className="dark h-12 shrink-0 flex items-center justify-between gap-3 px-3 bg-background text-foreground border-b z-10">
        <div className="flex items-center gap-3 min-w-0 overflow-hidden">
          <Button size="icon" variant="ghost" onClick={() => navigate("/team")} aria-label="Back to your sessions" title="Back to your sessions" data-testid="workspace-back"><ArrowLeft /></Button>
          <span className="font-display text-[15px] tracking-[0.02em] font-semibold">{session.short_id}</span>
          <EngineWithLogo engine={session.engine} className="gap-1.5 text-sm [&_svg]:w-3.5 [&_svg]:h-3.5" />
          <StatusBadge status={unlockToken ? "active" : session.status} />
          {!docs && <span className="font-mono text-xs text-muted-foreground truncate hidden sm:block">{session.branch}</span>}
        </div>
        {unlockToken && (
          // below lg the buttons show their icons only, so they never cover the session's name and badges
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" className={cn(headerButton, askOpen && "bg-white/15")} onClick={() => setAskOpen((v) => !v)} aria-pressed={askOpen}
              aria-label="Ask your documents" title="Ask your documents" data-testid="workspace-ask">
              <BookOpen /><span className="hidden lg:inline">Documents</span>
              {pdfCount > 0 && <span className="min-w-[18px] h-[18px] px-[5px] rounded bg-foreground text-background text-[11px] leading-[18px] text-center tabular-nums" data-testid="workspace-ask-count">{pdfCount}</span>}
            </Button>
            <Button variant="outline" className={headerButton} onClick={submit} disabled={submitting || !!working}
              aria-label="Submit for review" title="Submit for review" data-testid="workspace-submit">
              {submitting ? <Loader2 className="animate-spin" /> : <FileCheck />}<span className="hidden lg:inline">{submitting ? "Building the report…" : "Submit for review"}</span>
            </Button>
            {flags.app_preview && (
              <Button variant="outline" className={headerButton} onClick={openPreviewDialog} aria-label="See the app" title="See the app"
                data-testid="workspace-preview"><AppWindow /><span className="hidden lg:inline">App</span></Button>
            )}
            <Button variant="outline" className={headerButton} onClick={lock} aria-label="Lock" title="Lock" data-testid="workspace-lock">
              <Lock /><span className="hidden lg:inline">Lock</span></Button>
            <Button variant="outline" className={headerButton} onClick={end} disabled={ending} aria-label="End" title="End" data-testid="workspace-end">
              {ending ? <Loader2 className="animate-spin" /> : <LogOut />}<span className="hidden lg:inline">{ending ? "Ending…" : "End"}</span>
            </Button>
          </div>
        )}
      </header>

      {!unlockToken ? (
        <div className="flex-1 grid place-items-center p-6">
          <div className="w-full max-w-[380px] rounded-[10px] p-6 bg-card flex flex-col gap-3.5" data-testid="lock-screen">
            <h2 className="m-0 font-display font-semibold text-[22px] leading-[1.25] tracking-[0.02em]">Session locked</h2>
            {withPasskey ? (
              <div className="flex flex-col gap-3">
                <span className="text-gr-ink-secondary [text-wrap:pretty]">Use your passkey to unlock it. Only you can read this session.</span>
                <Button className="w-full" onClick={unlock} disabled={unlocking} data-testid="unlock-passkey">
                  {unlocking ? <Loader2 className="animate-spin" /> : <Fingerprint />}Unlock with passkey
                </Button>
                <span className="text-sm text-muted-foreground">Lost the device with your passkey? Remove it on your Account page with your password.</span>
              </div>
            ) : (
              <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); unlock(); }}>
                <span className="text-gr-ink-secondary [text-wrap:pretty]">Enter your password to unlock it. Only you can read this session.</span>
                <input type="password" aria-label="Your password" placeholder="Your password" value={pw} onChange={(e) => setPw(e.target.value)}
                  autoComplete="current-password" autoFocus data-testid="password-unlock-input"
                  className="h-8 w-full px-2.5 rounded-md border border-gr-control-border bg-transparent text-[13px] placeholder:text-muted-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring" />
                <Button type="submit" className="w-full" aria-disabled={!pw} disabled={unlocking} data-testid="unlock-submit">
                  {unlocking && <Loader2 className="animate-spin" />}Unlock
                </Button>
              </form>
            )}
            <span className="text-sm text-muted-foreground [text-wrap:pretty]">It locks again when nobody uses it for a while. Locking stops the {docs ? "agent" : "engine"} and backs up its work.</span>
          </div>
        </div>
      ) : (
        // on a phone the documents view scrolls as one page: the conversation fills the screen, the side panel follows
        <div className={cn("flex-1 min-h-0 flex flex-col lg:flex-row", docs && "overflow-y-auto lg:overflow-hidden")}>
          {/* the terminal keeps its monospace face (CLAUDE.md) */}
          {view === "diff" && (
            <DiffViewer sid={id} token={unlockToken} turn={diffTurn} onTurn={setDiffTurn} path={diffPath} onPath={setDiffPath}
              onClose={() => setView("term")} documents={docs} versions={versions} branch={session.branch} />
          )}
          {docs && (
            <div className={view === "diff" ? "hidden" : "shrink-0 h-[calc(100dvh-48px)] lg:h-auto lg:shrink lg:flex-1 min-w-0 lg:min-h-0 flex flex-col"} data-testid="documents-conversation">
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-5" data-testid="chat-stream">
                <div className="max-w-[720px] mx-auto flex flex-col gap-3.5">
                  <WelcomeCard shortId={session.short_id} company={user?.company_name} objective={objective} folders={session.folders}
                    tools={mcp.map((n) => n.charAt(0).toUpperCase() + n.slice(1))} pdfs={pdfCount > 0} />
                  {!entries.length && !working && (
                    <div className="text-sm text-muted-foreground text-center py-3 [text-wrap:pretty]">Nothing yet. Tell the agent what to change, for example “Add a section on cookies to the privacy policy.”</div>
                  )}
                  <DocTranscript entries={entries} versions={versions} stateOf={stateOf} engine={session.engine} onOpen={openDoc} onRetry={retry} />
                  {tidy(buffer) && (
                    <div className="flex gap-2.5 items-start" data-testid="msg-streaming">
                      <span aria-hidden="true" className="w-6 h-6 rounded-full bg-gr-surface-2 shrink-0" />
                      <div className="pt-0.5 min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere] text-gr-ink-secondary"><Inline text={tidy(buffer)} /></div>
                    </div>
                  )}
                  {working && <DocWorking since={working.since} tool={working.tool} engine={session.engine} stopping={stopping} />}
                </div>
              </div>
              <div className="shrink-0 border-t px-4 sm:px-6 pt-3 pb-3.5">
                <div className="max-w-[720px] mx-auto flex flex-col gap-2">
                  {low && (
                    <div role="status" className="flex gap-2 text-sm text-gr-warning"><TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>{usd(cap - spent)} left in this session's budget. The agent stops when it's used up.</span></div>
                  )}
                  {out && (
                    <div role="status" className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3.5 py-2.5 border rounded-lg text-sm" data-testid="documents-budget-out">
                      <span className="flex-[1_1_240px] flex gap-2 text-gr-danger"><CircleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span className="[text-wrap:pretty]">This session's budget is used up. Raise it in Session budget, or submit your work for review.</span></span>
                      <Button variant="outline" size="sm" onClick={() => { setCapDraft(String(cap)); setDialog("cap"); }}>Session budget</Button>
                    </div>
                  )}
                  {images.length > 0 && (
                    <div className="flex flex-wrap gap-2" data-testid="pending-images">
                      {images.map((img, i) => (
                        <div key={i} className="relative">
                          <img src={img.url} alt={`Screenshot ${i + 1}`} className="h-16 max-w-[120px] object-cover rounded border" />
                          <button type="button" aria-label={`Remove screenshot ${i + 1}`} onClick={() => setImages((now) => now.filter((_, j) => j !== i))}
                            className="absolute -top-1.5 -right-1.5 rounded-full bg-background border p-0.5"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={cn("border border-gr-control-border rounded-lg p-2 ps-3 flex flex-col gap-1.5", out && "opacity-40")} onDrop={onDrop} onDragOver={(e) => e.preventDefault()}>
                    <textarea ref={inputRef} rows={2} value={input} disabled={out}
                      onChange={(e) => { setInput(e.target.value); setHistoryAt(-1); }} onKeyDown={onKeyDown} onPaste={onPaste}
                      placeholder={out ? "The session budget is used up" : working ? "The agent is working…" : "Tell the agent what to change in your documents"}
                      aria-label="Message the agent" className="w-full bg-transparent resize-none outline-none leading-5 placeholder:text-muted-foreground"
                      data-testid="coder-session-workspace-chat-input" />
                    <div className="flex items-center justify-between gap-2">
                      <input ref={fileRef} type="file" accept={IMAGE_TYPES.join(",")} multiple hidden data-testid="image-file"
                        onChange={(e) => { addImages(e.target.files); e.target.value = ""; }} />
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" title="Add a screenshot (or paste one)" aria-label="Add a screenshot"
                        onClick={() => fileRef.current?.click()} data-testid="image-attach"><ImagePlus /></Button>
                      {working ? (
                        <Button variant="outline" onClick={stopTurn} disabled={stopping} data-testid="stop-turn"><Square className="w-3 h-3" />{stopping ? "Stopping…" : "Stop"}</Button>
                      ) : (
                        <Button onClick={send} aria-disabled={!input.trim() || out} data-testid="documents-send"><ArrowUp />Send</Button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 text-[11px] leading-4 text-muted-foreground tabular-nums">
                    <span>{modelLabel} · {usd(spent)} of {usd(cap)} used</span>
                    <span className="hidden sm:inline">Enter to send · Shift+Enter for a new line · Esc stops the agent</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!docs && <div className={view === "diff" ? "hidden" : "flex-1 min-w-0 min-h-0 flex flex-col font-mono text-[13px] leading-[21px]"}
            onClick={(e) => { if (!window.getSelection()?.toString() && e.target === e.currentTarget) inputRef.current?.focus(); }}>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-1.5" data-testid="chat-stream">
              <Banner session={session} objective={objective} memoryVersion={memory?.memory?.version} base={base} mcp={mcp} />
              {entries.map((e, i) => <Entry key={i} e={e} index={i} onOpenFile={openDiff} />)}
              {tidy(buffer) && (
                <div className="flex gap-2.5" data-testid="msg-streaming">
                  <span className="select-none">●</span>
                  <span className="whitespace-pre-wrap [overflow-wrap:anywhere] min-w-0 text-gr-ink-secondary"><Inline text={tidy(buffer)} /></span>
                </div>
              )}
              {working && <WorkingLine since={working.since} tool={working.tool} />}
            </div>
            <div className="shrink-0 px-5 pb-2.5">
              {matches.length > 0 && (
                <div className="pb-2 text-sm text-muted-foreground" data-testid="terminal-commands">
                  {matches.map(([c, d]) => <div key={c}><span className="text-foreground">{c}</span>  {d}</div>)}
                </div>
              )}
              {images.length > 0 && (
                <div className="flex flex-wrap gap-2 pb-2" data-testid="pending-images">
                  {images.map((img, i) => (
                    <div key={i} className="relative">
                      <img src={img.url} alt={`Screenshot ${i + 1}`} className="h-16 max-w-[120px] object-cover rounded border" />
                      <button type="button" aria-label={`Remove screenshot ${i + 1}`} onClick={() => setImages((now) => now.filter((_, j) => j !== i))}
                        className="absolute -top-1.5 -right-1.5 rounded-full bg-background border p-0.5"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-start gap-2.5 py-2 border-y" onDrop={onDrop} onDragOver={(e) => e.preventDefault()}>
                <span className="text-muted-foreground select-none">❯</span>
                <textarea ref={inputRef} rows={1} value={input} spellCheck={false}
                  onChange={(e) => { setInput(e.target.value); setHistoryAt(-1); }}
                  onKeyDown={onKeyDown} onPaste={onPaste}
                  placeholder={working ? "The engine is working…" : "Describe a task, or type / for commands"}
                  aria-label="Message the engine"
                  className="flex-1 min-w-0 bg-transparent resize-none outline-none placeholder:text-muted-foreground"
                  data-testid="coder-session-workspace-chat-input" />
                <input ref={fileRef} type="file" accept={IMAGE_TYPES.join(",")} multiple hidden data-testid="image-file"
                  onChange={(e) => { addImages(e.target.files); e.target.value = ""; }} />
                <button type="button" title="Add a screenshot (or paste one)" aria-label="Add a screenshot"
                  onClick={() => fileRef.current?.click()} className="shrink-0 text-muted-foreground hover:text-foreground" data-testid="image-attach">
                  <ImagePlus className="w-4 h-4" />
                </button>
                {working && (
                  <Button size="sm" variant="outline" className="shrink-0 h-7" onClick={stopTurn} disabled={stopping}
                    data-testid="stop-turn">{stopping ? "Stopping…" : "Stop"}</Button>
                )}
              </div>
              <div className="flex justify-between gap-3 pt-1.5 text-[11px] leading-4 text-muted-foreground">
                <span className="truncate"><span className="text-gr-warning">⏵⏵</span> {session.model} · {usd(spent)} of {usd(cap)}</span>
                <span className="shrink-0 hidden sm:inline">{working ? (stopping ? "stopping…" : "working… · esc to stop") : "enter to send · shift+enter for a new line · / for commands"}</span>
              </div>
            </div>
          </div>}

          {askOpen ? <AskDocuments sid={id} token={unlockToken} engine={session.engine} out={out} onClose={() => setAskOpen(false)} /> : (
          <aside className={cn("lg:w-[300px] shrink-0 border-t lg:border-t-0 lg:border-l flex flex-col lg:min-h-0 lg:overflow-y-auto",
            !docs && "min-h-0 max-h-[45vh] lg:max-h-none overflow-y-auto")}>
            <div className="p-4 border-b flex flex-col gap-2.5 tabular-nums">
              <div className="flex justify-between gap-2"><Label11>Session budget</Label11><span className="text-sm">{usd(spent)} of {usd(cap)}</span></div>
              <div className="h-1.5 rounded-[3px] bg-gr-surface-2 overflow-hidden" data-testid="workspace-budget-meter">
                <div className={out ? "h-full bg-gr-danger" : docs && low ? "h-full bg-gr-warning" : "h-full bg-foreground"} style={{ width: `${pct}%` }} />
              </div>
              {docs ? <>
                {low && <span className="text-sm text-gr-warning">Running low · {usd(cap - spent)} left</span>}
                {out && <span className="text-sm text-gr-danger">Budget reached</span>}
                <Button variant="outline" className="w-full" onClick={() => { setCapDraft(String(cap)); setDialog("cap"); }} data-testid="cap-open-btn">Change budget</Button>
              </> : (
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => { setCapDraft(String(cap)); setDialog("cap"); }} data-testid="cap-open-btn">Set cap</Button>
                  <Button variant="outline" className="flex-1" onClick={() => setDialog("propose")} data-testid="propose-open-btn">Propose</Button>
                </div>
              )}
            </div>
            {session && (
              <div className="p-4 border-b flex flex-col gap-2.5" data-testid="session-controls">
                <Label11>{docs ? "Next replies" : "Next turns"}</Label11>
                <div className="flex flex-col gap-1">
                  <span className="text-sm text-muted-foreground">Model</span>
                  <Select value={session.model} disabled={!!working}
                    onValueChange={(v) => setControls({ model: v }, docs ? "Model changed. It applies from the next reply." : `Model: ${v}, from the next turn.`)}>
                    <SelectTrigger aria-label="Model" data-testid="control-model"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(engineModels.some((m) => m.model_id === session.model) ? engineModels : [{ model_id: session.model, label: session.model }, ...engineModels])
                        .map((m) => <SelectItem key={m.model_id} value={m.model_id}>{m.label || m.model_id}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {(() => {
                  const levels = engineModels.find((m) => m.model_id === session.model)?.effort_levels || [];
                  return levels.length > 0 && (
                    <div className="flex flex-col gap-1">
                      <span className="text-sm text-muted-foreground">Reasoning effort</span>
                      <Select value={session.effort || "default"} disabled={!!working}
                        onValueChange={(v) => setControls({ effort: v === "default" ? "" : v }, docs ? "Reasoning effort changed. It applies from the next reply." : `Effort: ${v}, from the next turn.`)}>
                        <SelectTrigger aria-label="Reasoning effort" data-testid="control-effort"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">The model's default</SelectItem>
                          {levels.map((l) => <SelectItem key={l} value={l}>{l[0].toUpperCase() + l.slice(1)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })()}
                {session.engine === "claude" && (
                  <label className="flex items-center justify-between gap-2 cursor-pointer">
                    <span className="flex flex-col"><span className="text-sm">Plan mode</span>
                      <span className="text-xs text-muted-foreground">The agent plans and stops before changing anything.</span></span>
                    <Switch checked={!!session.plan_mode} disabled={!!working} data-testid="control-plan"
                      onCheckedChange={(v) => setControls({ plan_mode: v }, v ? "Plan mode on: the next turn ends with a plan." : "Plan mode off: the agent makes changes again.")} />
                  </label>
                )}
              </div>
            )}
            {docs ? <>
              <div className="p-4 border-b flex flex-col gap-2 text-sm" data-testid="documents-changed">
                <Label11>Documents changed in this session</Label11>
                {!sideDocs.length && <span className="text-muted-foreground">Nothing changed yet.</span>}
                {sideDocs.map((d) => (
                  <button key={d.path} type="button" onClick={() => openDoc(d.path, null)} title="See changes" data-testid={`side-doc-${d.path}`}
                    className="w-full flex items-start gap-2.5 px-2.5 py-2 border rounded-lg text-left hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
                    <span className="flex-1 min-w-0 flex flex-col"><span className="font-semibold text-[13px] [overflow-wrap:anywhere]">{docName(d.path)}</span>
                      <span className="text-muted-foreground">{versionsLabel(d.ns)}</span></span>
                    <DocTag state={stateOf(d.path)} className="mt-0.5" />
                  </button>
                ))}
                {sideDocs.length > 0 && (
                  <button type="button" onClick={() => openDoc(sideDocs[0].path, null)} data-testid="see-all-changes"
                    className="self-start -ms-1.5 h-7 px-1.5 inline-flex items-center gap-1 rounded-md font-semibold hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
                    See all changes<ChevronRight className="w-3.5 h-3.5 rtl:-scale-x-100" /></button>
                )}
              </div>
              <div className="p-4 border-b flex flex-col gap-2">
                <Button variant="outline" className="w-full" onClick={() => setDialog("propose")} data-testid="propose-open-btn"><Share />Propose to shared memory</Button>
                <span className="text-sm text-muted-foreground [text-wrap:pretty]">Share a fact or rule with every new session. Your founder approves it first.</span>
              </div>
            </> : <>
            <div className="px-4 pt-3">
                <Segmented label="Changes or commits" className="w-full [&>button]:flex-1" options={[["files", "Changes"], ["commits", "Commits"]]} value={tab} onChange={setTab} testid="tab" />
              </div>
              <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4 flex flex-col gap-2 text-sm">
                {tab === "files" ? (
                  files.length ? files.map((f, i) => (
                    <button key={`${f.path}:${i}`} type="button" onClick={() => openDiff(f.path)} title="See this file's changes"
                      className="border rounded-lg px-2.5 py-2 flex justify-between gap-2.5 text-left hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
                      data-testid={`file-change-${i}`}>
                      <span className="font-mono text-[11px] [overflow-wrap:anywhere] min-w-0">{f.path}</span>
                      <span className="whitespace-nowrap tabular-nums"><span className="text-gr-success">+{int(f.added)}</span> <span className="text-gr-danger">−{int(f.removed)}</span></span>
                    </button>
                  )) : <span className="text-muted-foreground">No changes yet.</span>
                ) : (
                  commits.length ? commits.map((c, i) => (
                    <div key={c.sha} className="border rounded-lg px-2.5 py-2 flex flex-col gap-0.5" data-testid={`commit-${i}`}>
                      <span className="font-mono text-[11px]">{c.sha}</span>
                      <span className="text-muted-foreground">{c.message}</span>
                    </div>
                  )) : <span className="text-muted-foreground">No commits yet.</span>
                )}
              </div>
            </>}
            <div className="p-4 border-t lg:mt-auto flex flex-col gap-1" data-testid="workspace-kind">
              <Label11>Kind of work</Label11>
              <span className="inline-flex items-center gap-1.5 font-semibold">
                {docs ? <FileText className="w-3.5 h-3.5" /> : <SquareTerminal className="w-3.5 h-3.5" />}{KIND_LABELS[workKind]}</span>
              <span className="text-sm text-muted-foreground [text-wrap:pretty]">{docs ? "Documents view" : "Terminal view"}, set by your founder for this session.</span>
            </div>
          </aside>
          )}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="gap-3.5">
          {dialog === "propose" && <>
            <DialogTitle>Propose to shared memory</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground [text-wrap:pretty]">
              A proposal is the only way anything from this session can reach your founder. It's added to shared memory for every new session only if they approve it.
            </DialogDescription>
            <Textarea rows={4} aria-label="Proposal" value={proposeText} onChange={(e) => setProposeText(e.target.value)}
              placeholder="A fact or convention worth sharing with every session" className="px-2.5 py-2 leading-5" data-testid="propose-text" />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
              <Button aria-disabled={!proposeText.trim()} onClick={() => proposeText.trim() && propose(proposeText.trim())} data-testid="propose-submit">Send proposal</Button>
            </div>
          </>}
          {dialog === "preview" && <>
            <DialogTitle>See the app</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground [text-wrap:pretty]">
              The app this session runs opens in a new tab, on a separate address, while the session stays unlocked. Only
              you can open it. Ask the agent to start the dev server if nothing is listed.
            </DialogDescription>
            {ports === null ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : ports.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="preview-ports">
                {ports.map((p) => (
                  <Button key={p} variant="outline" onClick={() => openPreview(p)} data-testid={`preview-port-${p}`}>Port {p}</Button>
                ))}
              </div>
            )}
            <form className="flex gap-2 items-center" onSubmit={(e) => { e.preventDefault(); if (Number(portDraft) > 0) openPreview(portDraft); }}>
              <input inputMode="numeric" aria-label="Port" placeholder="Port, e.g. 5173" value={portDraft}
                onChange={(e) => setPortDraft(e.target.value.replace(/\D/g, "").slice(0, 5))} data-testid="preview-port-input"
                className="h-8 flex-1 px-2.5 rounded-md border border-gr-control-border bg-transparent text-[13px] tabular-nums placeholder:text-muted-foreground" />
              <Button type="submit" aria-disabled={!(Number(portDraft) > 0)} data-testid="preview-open">Open</Button>
            </form>
          </>}
          {dialog === "cap" && <>
            <DialogTitle>{docs ? "Session budget" : "Session cap"}</DialogTitle>
            <div className="flex flex-col gap-1.5">
              <Dollars value={capDraft} onChange={setCapDraft} label={docs ? "Session budget in dollars" : "Session cap in dollars"} testid="cap-input" />
              <span className="text-sm text-muted-foreground">{docs
                ? `The agent stops when this session reaches it. Your daily and monthly budgets still apply. ${usd(spent)} is used so far.`
                : "The session stops when it reaches this. Your daily and monthly budgets still apply."}</span>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDialog(null)}>Cancel</Button>
              <Button aria-disabled={!(Number(capDraft) >= 1)} onClick={() => Number(capDraft) >= 1 && setCap(Number(capDraft))} data-testid="cap-save-btn">{docs ? "Save budget" : "Save cap"}</Button>
            </div>
          </>}
          {dialog === "report" && r && docs && (() => {
            // the report card in documents words (design_handoff_documents_view/screens/Your Change Report.dc.html)
            const vs = versionsOf(r.commits).reverse();
            return <>
              <DialogTitle>Submitted for review</DialogTitle>
              <span className="text-sm text-muted-foreground tabular-nums">Session {session.short_id} · {plural(r.totals.files, "document")} · {plural(vs.length, "version")}</span>
              <DialogDescription className="text-gr-ink-secondary [text-wrap:pretty]">
                Your founder gets this report with a summary written by AI from the changes. They see every changed document and the summary, never this conversation or your Ask your documents chat.
              </DialogDescription>
              {pr && !pr.number && pr.reason && <span className="text-sm text-gr-warning">No review on GitHub yet. {pr.reason}</span>}
              <div className="flex flex-col gap-2">
                <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Every changed document</span>
                <div className="flex flex-col gap-px bg-gr-hairline border rounded-lg overflow-hidden text-sm" data-testid="change-report">
                  {r.files.length === 0 && <div className="bg-background px-3 py-2 text-muted-foreground">No changes</div>}
                  {r.files.map((f) => {
                    const [word, tone] = DOC_STATE[f.status] || DOC_STATE.modified;
                    return (
                      <div key={f.path} className="bg-background flex items-center justify-between gap-3 px-3 py-2">
                        <span className="flex flex-col min-w-0"><span className="font-semibold text-[13px] [overflow-wrap:anywhere]">{docName(f.path)}</span>
                          <span className="text-muted-foreground [overflow-wrap:anywhere]">{docFolder(f.path) || "Top folder"}</span></span>
                        <DocTag state={word.toLowerCase()} />
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Versions</span>
                {vs.map((v) => (
                  <div key={v.sha} className="flex items-start gap-2 text-sm">
                    <History className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                    <span className="flex-1 min-w-0"><span className="font-semibold">Version {v.n}</span><span className="text-muted-foreground"> · {versionDetail(v)}</span></span>
                  </div>
                ))}
              </div>
              <Disclosure testid="report-technical-details">
                <dl className="m-0 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
                  <dt className="text-muted-foreground">Branch</dt><dd className="m-0 font-mono text-[12px] [overflow-wrap:anywhere]" dir="ltr">{r.branch}</dd>
                  {pr?.number && <><dt className="text-muted-foreground">Pull request</dt><dd className="m-0 tabular-nums">#{pr.number}</dd></>}
                  {vs.map((v) => <Fragment key={v.sha}><dt className="text-muted-foreground">Version {v.n}</dt>
                    <dd className="m-0 [overflow-wrap:anywhere]" dir="ltr"><span className="font-mono text-[12px]">{v.sha.slice(0, 7)}</span><span className="text-muted-foreground"> · {v.message}</span></dd></Fragment>)}
                </dl>
              </Disclosure>
              <div className="flex justify-end"><Button onClick={() => setDialog(null)}>Done</Button></div>
            </>;
          })()}
          {dialog === "report" && r && !docs && <>
            <DialogTitle>Submitted for review</DialogTitle>
            <span className="text-sm text-muted-foreground tabular-nums">
              <span className="font-mono text-foreground">{r.branch}</span> · {plural(r.totals.files, "file")} +{int(r.totals.added_lines)} −{int(r.totals.removed_lines)}
              {pr?.number && ` · pull request #${pr.number}`}
            </span>
            <DialogDescription className="text-gr-ink-secondary [text-wrap:pretty]">
              {APP_NAME} built this report from git{pr?.number ? " and made it the pull request's description" : ""}. Your founder sees every changed file and the summary, never your prompts or the agent's replies.
            </DialogDescription>
            {pr && !pr.number && pr.reason && <span className="text-sm text-gr-warning">No pull request yet. {pr.reason}</span>}
            <div className="flex flex-col gap-px bg-gr-hairline border rounded-lg overflow-hidden text-sm" data-testid="change-report">
              {r.files.length === 0 && <div className="bg-card px-3 py-2 text-muted-foreground">No changes</div>}
              {r.files.map((f) => (
                <div key={f.path} className="bg-card flex justify-between gap-3 px-3 py-2">
                  <span className="font-mono [overflow-wrap:anywhere] min-w-0">{f.path}</span>
                  <span className="whitespace-nowrap tabular-nums"><span className="text-gr-success">+{int(f.added)}</span> <span className="text-gr-danger">−{int(f.removed)}</span></span>
                </div>
              ))}
            </div>
            <div className="flex justify-end"><Button onClick={() => setDialog(null)}>Done</Button></div>
          </>}
        </DialogContent>
      </Dialog>
    </GraphiteScope>
  );
}
